import React from 'react';
import { Search, RefreshCw, Loader2, Download } from 'lucide-react';
import { TicketListItem, Ticket } from './TicketListItem';

interface TicketListProps {
  tickets: Ticket[];
  selectedTicketId?: string;
  onTicketSelect?: (ticketId: string) => void;
  loading?: boolean;
  onRefresh?: () => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSync?: () => void;
  syncing?: boolean;
}

export function TicketList({
  tickets,
  selectedTicketId,
  onTicketSelect,
  loading,
  onRefresh,
  searchQuery,
  onSearchChange,
  onSync,
  syncing,
}: TicketListProps) {
  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200/60">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200/80 bg-white">
        {/* Search Bar */}
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400/50 focus:bg-white transition-all placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-3 pb-2.5">
          <span className="text-xs font-medium text-gray-500">
            {tickets.length} {tickets.length === 1 ? 'ticket' : 'tickets'}
          </span>

          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw
              size={14}
              className={`text-gray-500 ${loading ? 'animate-spin' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Ticket List */}
      <div className="flex-1 overflow-y-auto">
        {loading && tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48">
            <Loader2 className="animate-spin text-gray-300" size={24} />
            <span className="text-xs text-gray-400 mt-3">Loading tickets...</span>
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-500 px-6">
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
              <Search size={18} className="text-gray-400" />
            </div>
            <p className="text-[13px] font-medium text-gray-600">No tickets yet</p>
            {searchQuery ? (
              <p className="text-xs text-gray-400 mt-1 text-center">Try adjusting your search or filters</p>
            ) : onSync ? (
              <button
                onClick={onSync}
                disabled={syncing}
                className="mt-3 flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-xs font-medium rounded-xl hover:bg-amber-600 disabled:opacity-50 transition-colors shadow-sm"
              >
                {syncing ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Download size={14} />
                )}
                {syncing ? 'Syncing...' : 'Sync from Inbox'}
              </button>
            ) : null}
          </div>
        ) : (
          tickets.map((ticket) => (
            <TicketListItem
              key={ticket.ticket_id}
              ticket={ticket}
              selected={selectedTicketId === ticket.ticket_id}
              onClick={() => onTicketSelect?.(ticket.ticket_id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
